import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    NEWS_FILTERS,
    NewsFilter,
    NewsItem,
    getYouTubeEmbedUrl,
    getYouTubeThumbnail,
    getYouTubeWatchUrl,
    newsItems,
} from '../data/newsItems';

const PUREMEDIFY_YOUTUBE_CHANNEL = 'https://www.youtube.com/@puremedify';

const NewsCard = ({ item, index, onPlayVideo }: { item: NewsItem; index: number; onPlayVideo: (item: NewsItem) => void }) => {
    const isVideo = item.type === 'video';
    const thumbnail = isVideo && item.youtubeId ? getYouTubeThumbnail(item.youtubeId) : item.image;

    return (
        <article
            className="animate-fade-in group flex flex-col overflow-hidden rounded-2xl bg-white/80 shadow-lg transition-transform duration-300 hover:-translate-y-2 hover:shadow-2xl"
            style={{ animationDelay: `${0.2 + index * 0.08}s` }}
        >
            <div className="relative h-44 w-full overflow-hidden">
                <img
                    src={thumbnail}
                    alt={item.title}
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {isVideo && (
                    <>
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                        <button
                            type="button"
                            onClick={() => onPlayVideo(item)}
                            className="absolute inset-0 flex items-center justify-center"
                            aria-label={`Play video: ${item.title}`}
                        >
                            <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/95 text-lochmara-blue shadow-lg transition-transform group-hover:scale-110">
                                <svg className="ml-1 h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                    <path d="M8 5v14l11-7z" />
                                </svg>
                            </span>
                        </button>
                        <span className="absolute left-3 top-3 rounded-full bg-red-600 px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                            Video
                        </span>
                    </>
                )}
            </div>
            <div className="flex flex-1 flex-col p-6">
                <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="inline-block rounded-full bg-lochmara-blue/10 px-3 py-1 text-xs font-bold text-lochmara-blue">
                        {item.date}
                    </span>
                    {item.location && (
                        <span className="inline-block rounded-full bg-teal-100 px-3 py-1 text-xs font-medium text-teal-800">
                            {item.location}
                        </span>
                    )}
                </div>
                <h2 className="mb-2 text-xl font-bold text-lochmara-blue">{item.title}</h2>
                <p className="mb-4 flex-1 font-redhat text-gray-700">{item.summary}</p>
                {isVideo && (
                    <button
                        type="button"
                        onClick={() => onPlayVideo(item)}
                        className="inline-flex w-fit items-center gap-2 rounded-full border border-lochmara-blue/20 px-4 py-2 text-sm font-semibold text-lochmara-blue transition hover:bg-lochmara-blue/5"
                    >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M8 5v14l11-7z" />
                        </svg>
                        {item.youtubeId ? 'Watch video' : 'Video coming soon'}
                    </button>
                )}
            </div>
        </article>
    );
};

const News = () => {
    const [filter, setFilter] = useState<NewsFilter>('All');
    const [activeVideo, setActiveVideo] = useState<NewsItem | null>(null);

    const filteredItems = useMemo(() => {
        if (filter === 'Stories') return newsItems.filter((item) => item.type === 'article');
        if (filter === 'Videos') return newsItems.filter((item) => item.type === 'video');
        return newsItems;
    }, [filter]);

    useEffect(() => {
        if (!activeVideo) return;
        const handler = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setActiveVideo(null);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [activeVideo]);

    return (
        <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 to-teal-100">
            {/* Hero Section */}
            <section className="from-brand-light relative flex w-full flex-col items-center justify-center overflow-hidden bg-gradient-to-br to-blue-100 py-16 md:py-24">
                <div className="relative z-10 mx-auto max-w-3xl px-4 text-center">
                    <h1 className="animate-fade-in mb-4 font-ubuntu text-4xl font-bold text-lochmara-blue drop-shadow-lg md:text-5xl">
                        News & Updates
                    </h1>
                    <p className="animate-fade-in mb-6 font-redhat text-xl text-gray-700 delay-200 md:text-2xl">
                        Stay informed with updates on our latest services, community outreach, health campaigns, and event videos from across Africa.
                    </p>
                </div>
                <svg className="animate-float-slow absolute left-10 top-10 z-0 h-24 w-24 opacity-30" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="40" fill="#0087ca" /></svg>
                <svg className="animate-float-medium absolute right-20 top-24 z-0 h-16 w-16 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="35" fill="#00b937" /></svg>
                <svg className="animate-float-fast absolute bottom-10 left-1/2 z-0 h-20 w-20 opacity-20" viewBox="0 0 100 100" fill="none"><circle cx="50" cy="50" r="30" fill="#004487" /></svg>
                <svg className="animate-float-cross absolute bottom-16 right-10 z-0 h-16 w-16" viewBox="0 0 64 64" fill="none"><rect x="28" y="8" width="8" height="48" rx="4" fill="#0087ca" /><rect x="8" y="28" width="48" height="8" rx="4" fill="#00b937" /></svg>
            </section>

            {/* Filter + YouTube banner */}
            <section className="w-full px-4 pt-10">
                <div className="mx-auto flex max-w-5xl flex-col gap-6">
                    <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
                        <div className="flex flex-wrap gap-2">
                            {NEWS_FILTERS.map((option) => (
                                <button
                                    key={option}
                                    type="button"
                                    onClick={() => setFilter(option)}
                                    className={`rounded-full px-5 py-2 text-sm font-semibold transition ${
                                        filter === option
                                            ? 'bg-lochmara-blue text-white shadow-md'
                                            : 'bg-white/80 text-lochmara-blue hover:bg-white'
                                    }`}
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                        <a
                            href={PUREMEDIFY_YOUTUBE_CHANNEL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 rounded-full bg-red-600 px-5 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-red-700"
                        >
                            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                                <path d="M23.5 6.2c-.3-1.1-1.2-2-2.3-2.2C19.2 3.6 12 3.6 12 3.6s-7.2 0-9.2.4C1.7 4.2.8 5.1.5 6.2.1 8.2 0 12 0 12s.1 3.8.5 5.8c.3 1.1 1.2 2 2.3 2.2 2 .4 9.2.4 9.2.4s7.2 0 9.2-.4c1.1-.2 2-1.1 2.3-2.2.4-2 .5-5.8.5-5.8s-.1-3.8-.5-5.8zM9.6 15.6V8.4L15.8 12 9.6 15.6z" />
                            </svg>
                            Subscribe on YouTube
                        </a>
                    </div>

                    <div className="rounded-2xl border border-lochmara-blue/10 bg-white/70 p-5 shadow-sm">
                        <p className="font-redhat text-sm text-gray-700 md:text-base">
                            Event recaps and outreach footage from our mobile clinics, health workshops, and community campaigns are published here as YouTube videos. Subscribe to our channel for the latest recordings from the field.
                        </p>
                    </div>
                </div>
            </section>

            {/* News Grid */}
            <section className="w-full px-4 py-10">
                <div className="mx-auto grid max-w-5xl grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
                    {filteredItems.map((item, idx) => (
                        <NewsCard key={item.id} item={item} index={idx} onPlayVideo={setActiveVideo} />
                    ))}
                </div>
                {filteredItems.length === 0 && (
                    <p className="mx-auto max-w-2xl text-center font-redhat text-gray-600">
                        No items in this category yet. Check back soon for updates.
                    </p>
                )}
            </section>

            {/* Video Modal */}
            {activeVideo && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setActiveVideo(null)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={activeVideo.title}
                >
                    <div
                        className="relative w-full max-w-4xl overflow-hidden rounded-2xl bg-white shadow-2xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            type="button"
                            onClick={() => setActiveVideo(null)}
                            className="absolute right-3 top-3 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-black/60 text-white transition hover:bg-black/80"
                            aria-label="Close video"
                        >
                            ×
                        </button>
                        {activeVideo.youtubeId ? (
                            <div className="aspect-video w-full bg-black">
                                <iframe
                                    title={activeVideo.title}
                                    src={getYouTubeEmbedUrl(activeVideo.youtubeId)}
                                    className="h-full w-full"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                />
                            </div>
                        ) : (
                            <div className="flex flex-col items-center gap-4 px-6 py-12 text-center">
                                <img
                                    src={activeVideo.image}
                                    alt={activeVideo.title}
                                    className="h-48 w-full max-w-md rounded-xl object-cover"
                                />
                                <h3 className="font-ubuntu text-2xl font-bold text-lochmara-blue">{activeVideo.title}</h3>
                                <p className="max-w-lg font-redhat text-gray-700">{activeVideo.summary}</p>
                                <p className="font-redhat text-sm text-gray-500">
                                    This event video will be published on our YouTube channel soon.
                                </p>
                                <a
                                    href={PUREMEDIFY_YOUTUBE_CHANNEL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-2 rounded-full bg-red-600 px-6 py-3 font-semibold text-white transition hover:bg-red-700"
                                >
                                    Visit our YouTube channel
                                </a>
                            </div>
                        )}
                        <div className="border-t border-gray-100 px-6 py-4">
                            <h3 className="font-ubuntu text-lg font-bold text-lochmara-blue">{activeVideo.title}</h3>
                            <p className="mt-1 font-redhat text-sm text-gray-600">{activeVideo.summary}</p>
                            {activeVideo.youtubeId && (
                                <a
                                    href={getYouTubeWatchUrl(activeVideo.youtubeId)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="mt-3 inline-flex text-sm font-semibold text-lochmara-blue hover:underline"
                                >
                                    Open on YouTube
                                </a>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Call to Action Section */}
            <section className="flex w-full flex-col items-center bg-gradient-to-br from-blue-100 to-teal-50 px-4 py-12">
                <h2 className="mb-4 text-center font-ubuntu text-2xl font-bold text-congress-blue md:text-3xl">Want the Latest Updates?</h2>
                <p className="mb-6 max-w-2xl text-center font-redhat text-lg text-gray-700">
                    Follow us on social media, subscribe on YouTube, or contact us to stay up to date with our latest news, projects, and impact stories.
                </p>
                <div className="flex flex-wrap justify-center gap-4">
                    <Link to="/contact" className="rounded-full bg-brand px-8 py-4 font-ubuntu text-lg font-semibold text-white shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-brand-green">
                        Contact Us
                    </Link>
                    <a
                        href={PUREMEDIFY_YOUTUBE_CHANNEL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-full border-2 border-red-600 px-8 py-4 font-ubuntu text-lg font-semibold text-red-600 shadow-lg transition-transform duration-200 hover:scale-105 hover:bg-red-50"
                    >
                        YouTube Channel
                    </a>
                </div>
            </section>

            <style>{`
                @keyframes fade-in { 0% { opacity: 0; transform: translateY(40px);} 100% { opacity: 1; transform: translateY(0);} }
                .animate-fade-in { animation: fade-in 1.2s cubic-bezier(.4,0,.2,1) both; }
                .delay-200 { animation-delay: 0.2s; }
                @keyframes float-slow { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-20px); } }
                .animate-float-slow { animation: float-slow 7s ease-in-out infinite; }
                @keyframes float-medium { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-30px); } }
                .animate-float-medium { animation: float-medium 5s ease-in-out infinite; }
                @keyframes float-fast { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-40px); } }
                .animate-float-fast { animation: float-fast 3.5s ease-in-out infinite; }
                @keyframes float-cross { 0%, 100% { transform: translateY(0) rotate(-8deg); } 50% { transform: translateY(-18px) rotate(8deg); } }
                .animate-float-cross { animation: float-cross 6s ease-in-out infinite; }
            `}</style>
        </div>
    );
};

export default News;
